import { SITE_URL, SeoPagina, tipoSchemaPorRubro } from '../shared/seo/seo.service';
import { MarcaDetalle, MarcaResumen } from './services/marketplace-api.service';

/**
 * SEO de las páginas del marketplace: textos y datos estructurados (schema.org).
 *
 * **Nunca se publica `aggregateRating`.** El rating del negocio es una columna que se carga
 * a mano (`MktPerfil.Rating`), no un promedio de reseñas de clientes: hoy los negocios
 * dicen 5 estrellas con 0 reseñas. Marcar eso para Google es "reseñas no respaldadas", una
 * de las causas de acción manual. Se agrega el día que existan reseñas reales.
 */

const soles = (n: number) => `S/ ${Number(n).toFixed(Number(n) % 1 ? 2 : 0)}`;

function rangoPrecios(servicios: { precioBruto: number }[]): string | undefined {
  const precios = servicios.map((s) => Number(s.precioBruto)).filter((n) => n > 0);
  if (!precios.length) return undefined;
  const min = Math.min(...precios);
  const max = Math.max(...precios);
  return min === max ? soles(min) : `${soles(min)} – ${soles(max)}`;
}

function listaServicios(servicios: { nombre: string }[], max = 4): string {
  const n = servicios.slice(0, max).map((s) => s.nombre);
  return n.length ? n.join(', ') + (servicios.length > max ? ' y más' : '') : '';
}

export function seoHome(marcas: MarcaResumen[]): SeoPagina {
  return {
    titulo: 'Aura · Reservá tu cita en spas, clínicas y salones de Lima',
    descripcion:
      'Encontrá centros de estética, spas, clínicas, veterinarias y salones en Lima. ' +
      'Compará servicios y precios, mirá horarios y reservá online.',
    path: '/marketplace',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Aura',
        url: SITE_URL,
        inLanguage: 'es-PE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: marcas.map((m, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${SITE_URL}/marketplace/${m.slug}`,
          name: m.nombre,
        })),
      },
    ],
  };
}

export function seoMarca(m: MarcaDetalle): SeoPagina {
  const path = `/marketplace/${m.slug}`;
  const distritos = [...new Set((m.sedes ?? []).map((s) => s.distrito).filter(Boolean))];
  const donde = distritos.length ? ` en ${distritos.join(', ')}` : '';
  const servicios = listaServicios(m.servicios ?? []);
  const redes = m.redes;
  return {
    titulo: `${m.nombre}${donde}: precios y reservas`,
    descripcion:
      (m.descripcion ? `${m.descripcion} ` : '') +
      (servicios ? `Servicios: ${servicios}. ` : '') +
      'Reservá online.',
    path,
    imagen: m.portadaUrl || m.logoUrl,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: m.nombre,
      url: `${SITE_URL}${path}`,
      description: m.descripcion || undefined,
      logo: m.logoUrl || undefined,
      sameAs: [redes?.instagram, redes?.facebook, redes?.tiktok, redes?.web].filter(Boolean),
      subOrganization: (m.sedes ?? []).map((s) => ({
        '@type': tipoSchemaPorRubro(m.rubro),
        name: `${m.nombre} ${s.nombre}`,
        url: `${SITE_URL}${path}/${s.slug}`,
        address: direccion(s),
      })),
    },
  };
}

export function seoSede(m: MarcaDetalle, s: any): SeoPagina {
  const path = `/marketplace/${m.slug}/${s.slug}`;
  const servicios = (s.servicios ?? m.servicios ?? []) as any[];
  const lista = listaServicios(servicios);
  const precios = rangoPrecios(servicios);
  const telefono = s.telefono || s.whatsapp || undefined;
  return {
    titulo: `${m.nombre} en ${s.distrito || s.nombre}: horarios, precios y reservas`,
    descripcion:
      `${m.nombre} ${s.nombre}, ${s.direccion}${s.distrito ? `, ${s.distrito}` : ''}. ` +
      (lista ? `${lista}${precios ? ` desde ${precios.split(' – ')[0]}` : ''}. ` : '') +
      'Mirá los horarios libres y reservá online.',
    path,
    imagen: m.portadaUrl || m.logoUrl,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': tipoSchemaPorRubro(m.rubro),
        name: `${m.nombre} ${s.nombre}`,
        url: `${SITE_URL}${path}`,
        description: m.descripcion || undefined,
        image: m.portadaUrl || m.logoUrl || undefined,
        telephone: telefono,
        priceRange: precios,
        address: direccion(s),
        geo:
          s.lat != null && s.lng != null
            ? { '@type': 'GeoCoordinates', latitude: Number(s.lat), longitude: Number(s.lng) }
            : undefined,
        parentOrganization: { '@type': 'Organization', name: m.nombre, url: `${SITE_URL}/marketplace/${m.slug}` },
        makesOffer: servicios.map((x) => ({
          '@type': 'Offer',
          price: Number(x.precioBruto),
          priceCurrency: 'PEN',
          itemOffered: {
            '@type': 'Service',
            name: x.nombre,
            description: x.descripcion || undefined,
          },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Aura', item: `${SITE_URL}/marketplace` },
          { '@type': 'ListItem', position: 2, name: m.nombre, item: `${SITE_URL}/marketplace/${m.slug}` },
          { '@type': 'ListItem', position: 3, name: s.nombre, item: `${SITE_URL}${path}` },
        ],
      },
    ],
  };
}

function direccion(s: any) {
  return {
    '@type': 'PostalAddress',
    streetAddress: s.direccion || undefined,
    addressLocality: s.distrito || undefined,
    addressRegion: 'Lima',
    addressCountry: 'PE',
  };
}
